/**
 * TSPL Builder for Label Printing
 * Creates TSPL (TSC Programming Language) commands for QZ Tray printing
 * TSPL uses dots as unit (203 DPI = 8 dots/mm, 300 DPI = 12 dots/mm)
 */

import dayjs from 'dayjs'
import { isOptionEnabled } from '../helper'
import { getTableReceiptLabel } from './receiptTableHelper'

// DPI conversion: 203 DPI = 8 dots/mm, 300 DPI = 12 dots/mm
const DOTS_PER_MM_203 = 8 // Standard for most TSPL printers
const DOTS_PER_MM_300 = 12 // High resolution printers

/**
 * Preset keys untuk ukuran kertas label (digunakan untuk UI/options)
 */
const LABEL_PRESET_KEYS = [
  '33x15mm',
  '33x19mm',
  '38x60mm',
  '40x30mm',
  '40x40mm',
  '50x20mm',
  '50x30mm',
  '55x20mm',
  '55x30mm',
  '55x40mm',
  '60x40mm',
  '70x30mm',
  '78x60mm',
]

/**
 * Ambil widthMm dan heightMm dari string paper size (preset atau custom).
 * Format: "WxHmm" atau "WxH" (contoh: "40x30mm", "80x100mm", "40x30")
 * @param {string} paperSize - String ukuran kertas
 * @returns {{ widthMm: number, heightMm: number } | null}
 */
export const getLabelDimensions = (paperSize) => {
  if (!paperSize) {
    return null
  }

  const sizeStr = String(paperSize).toLowerCase().replace(/mm/g, '').trim()
  const match = sizeStr.match(/(\d+)x(\d+)/)

  if (match) {
    const widthMm = parseInt(match[1], 10)
    const heightMm = parseInt(match[2], 10)
    if (widthMm > 0 && heightMm > 0) {
      return { widthMm, heightMm }
    }
  }

  return null
}

/**
 * LABEL_PRESETS - object preset untuk backward compatibility.
 * Generated dari LABEL_PRESET_KEYS via getLabelDimensions.
 */
export const LABEL_PRESETS = Object.fromEntries(
  LABEL_PRESET_KEYS.map((key) => [key, getLabelDimensions(key)]).filter(([, v]) => v != null),
)

/**
 * Convert mm to dots (TSPL unit)
 * @param {number} mm - Millimeters
 * @param {number} dpi - DPI (default: 203)
 * @returns {number} Dots
 */
const mmToDots = (mm, dpi = 203) => {
  const dotsPerMm = dpi === 300 ? DOTS_PER_MM_300 : DOTS_PER_MM_203
  return Math.round(mm * dotsPerMm)
}

/**
 * Escape special characters for TSPL
 */
const escapeTSPL = (text) => {
  if (!text) {
    return ''
  }
  return String(text).replace(/"/g, '""') // Double quotes need to be escaped
}

/**
 * Wrap text by words to fit within character width
 * @param {string} text - Text to wrap
 * @param {number} maxChars - Maximum characters per line
 * @returns {Array<string>} Array of wrapped lines
 */
const wrapWords = (text = '', maxChars = 20) => {
  const t = String(text ?? '').trim()
  if (!t) {
    return ['']
  }

  // Ensure maxChars is at least 1
  const safeMaxChars = Math.max(1, Math.floor(maxChars))

  // If text fits in one line, return as is
  if (t.length <= safeMaxChars) {
    return [t]
  }

  const words = t.split(/\s+/).filter((w) => w.length > 0)
  if (words.length === 0) {
    return ['']
  }

  const lines = []
  let currentLine = ''

  words.forEach((word) => {
    // If single word is longer than maxChars, split it
    if (word.length > safeMaxChars) {
      // Push current line if exists
      if (currentLine.trim()) {
        lines.push(currentLine.trim())
        currentLine = ''
      }
      // Split long word character by character
      let remainingWord = word
      while (remainingWord.length > safeMaxChars) {
        lines.push(remainingWord.substring(0, safeMaxChars))
        remainingWord = remainingWord.substring(safeMaxChars)
      }
      if (remainingWord) {
        currentLine = remainingWord
      }
      return
    }

    // Try to add word to current line
    const testLine = currentLine ? `${currentLine} ${word}` : word

    if (testLine.length <= safeMaxChars) {
      currentLine = testLine
      return
    }

    // Current line is full, push it and start new line with current word
    if (currentLine.trim()) {
      lines.push(currentLine.trim())
    }
    currentLine = word
  })

  // Push remaining line
  if (currentLine.trim()) {
    lines.push(currentLine.trim())
  }

  return lines.length > 0 ? lines : [t] // Fallback to original text if no lines
}

/**
 * Build TSPL command for a single label
 * Returns array of strings (like buildReceiptCommands in escposBuilder.js)
 * @param {Object} labelData - Label data { sequence, qty, productName, contactName, timestampText? }
 * @param {Object} options - Options { paperWidth, dpi, direction }
 * @returns {Array<string>} Array of TSPL command strings, each ending with \r\n
 */
export const buildTSPLLabel = (labelData, options = {}) => {
  const { paperWidth, dpi = 203, direction = 0 } = options

  // Parse paper size (preset atau custom format "WxHmm")
  const paperSize = getLabelDimensions(paperWidth)
  if (!paperSize) {
    // Default to 40x30mm
    return buildTSPLLabel(labelData, { ...options, paperWidth: '40x30mm' })
  }

  const { widthMm, heightMm } = paperSize

  // TSPL commands array - use \r\n for proper line endings
  const commands = [] // Wake up command (kosong)

  // Use actual printer size: 30x20mm (after calibration)
  // But keep using paperWidth from config for flexibility
  const actualWidthMm = widthMm // Use configured width
  const actualHeightMm = heightMm // Use configured height

  commands.push(`SIZE ${actualWidthMm} mm,${actualHeightMm} mm\r\n`) // Set Ukuran Label
  commands.push(`GAP 3 mm,0 mm\r\n`) // Set Jarak Gap
  commands.push(`DIRECTION ${direction}\r\n`) // Arah cetak (0=normal, 1=rotated 180)
  commands.push(`CLS\r\n`) // Bersihkan memori (Clear Screen)

  // Calculate positions (in dots)
  const widthDots = mmToDots(actualWidthMm, dpi)
  const leftX = mmToDots(2, dpi) // Left align: 2mm from left edge

  // Font numbers: "1"=small, "2"=medium, "3"=large/bold
  // Use font "2" for product name (medium, better readability)
  // Use font "2" for contact and timestamp (medium)
  const productFont = '2' // Medium font for product name
  const contactFont = '2' // Medium font for contact
  const timestampFont = '2' // Medium font for timestamp

  // Calculate max characters per line based on label width
  // Font "2" (medium): approximately 8-9 dots per character (for 203 DPI)
  // Available width = widthDots - leftX - rightMargin
  const rightMargin = mmToDots(2, dpi) // 2mm right margin for product/contact text
  const availableWidthDots = widthDots - leftX - rightMargin

  // Perhitungan konservatif untuk font "2" agar teks wrap dengan benar (tidak terpotong)
  // Font "2" (medium) bisa lebih lebar dari perkiraan - gunakan 12 dots/char untuk aman
  // sehingga nama produk panjang seperti "buku tulis 100 lembar" akan wrap ke baris berikutnya
  const calculatedMaxChars = Math.floor(availableWidthDots / 12)

  let maxCharsPerLine
  if (actualWidthMm <= 30) {
    // Label 30mm: sangat konservatif agar tidak terpotong
    maxCharsPerLine = Math.min(14, calculatedMaxChars)
  } else if (actualWidthMm <= 40) {
    // Label 40mm: konservatif agar "buku tulis 100 lembar" wrap ke "lembar" di baris baru
    maxCharsPerLine = Math.min(20, calculatedMaxChars)
  } else if (actualWidthMm <= 50) {
    // Label 50mm
    maxCharsPerLine = Math.min(26, calculatedMaxChars)
  } else {
    // Label besar
    maxCharsPerLine = Math.min(20, calculatedMaxChars)
  }

  // Minimum value
  maxCharsPerLine = Math.max(14, maxCharsPerLine)

  // Separate right margin for timestamp (more space on right)
  const timestampRightMargin = mmToDots(3, dpi) // 3mm right margin for timestamp
  // Jarak vertikal antar baris saat wrap - 28 dots (~3.5mm) agar tidak terlalu mepet
  const lineHeight = 28 // 28 dots spacing between lines (~3.5mm for 203 DPI)

  // Y coordinates from top (in dots)
  // Start from top: Y=10 dots (~1.25mm for 203 DPI)
  let currentY = 10 // Start from top

  // Product name with sequence (FIRST - at the top, LEFT aligned, with wrap)
  const productLabel = `(${labelData.sequence}/${labelData.qty}) ${labelData.productName || '-'}`
  const productLines = wrapWords(productLabel, maxCharsPerLine)

  // Print each line of product name
  productLines.forEach((line) => {
    if (line && line.trim()) {
      const escapedProduct = escapeTSPL(line.trim())
      commands.push(`TEXT ${leftX},${currentY},"${productFont}",0,1,1,"${escapedProduct}"\r\n`)
      currentY += lineHeight // Move down for next line
    }
  })

  // Description if exists (SECOND - below product name, LEFT aligned)
  // Uses same maxCharsPerLine calculation as product name for consistent wrapping
  if (labelData.desc) {
    const descLine = `=> ${labelData.desc}`
    const descLines = wrapWords(descLine, maxCharsPerLine) // Same maxCharsPerLine as product name

    // Print each line of description
    descLines.forEach((line) => {
      if (line && line.trim()) {
        const escapedDesc = escapeTSPL(line.trim())
        commands.push(`TEXT ${leftX},${currentY},"${contactFont}",0,1,1,"${escapedDesc}"\r\n`)
        currentY += lineHeight // Move down for next line
      }
    })
  }

  // Contact name if exists (THIRD - below description, LEFT aligned)
  // Uses same maxCharsPerLine calculation as product name for consistent wrapping
  if (labelData.contactName) {
    const contactLine = `=> ${labelData.contactName}`
    const contactLines = wrapWords(contactLine, maxCharsPerLine) // Same maxCharsPerLine as product name

    // Print each line of contact name
    contactLines.forEach((line) => {
      if (line && line.trim()) {
        const escapedContact = escapeTSPL(line.trim())
        commands.push(`TEXT ${leftX},${currentY},"${contactFont}",0,1,1,"${escapedContact}"\r\n`)
        currentY += lineHeight // Move down for next line
      }
    })
  }

  // Detail meja (order Table) if exists — below contact, LEFT aligned
  if (labelData.tableLabel) {
    const tableLine = `=> ${labelData.tableLabel}`
    const tableLines = wrapWords(tableLine, maxCharsPerLine)

    tableLines.forEach((line) => {
      if (line && line.trim()) {
        const escapedTable = escapeTSPL(line.trim())
        commands.push(`TEXT ${leftX},${currentY},"${contactFont}",0,1,1,"${escapedTable}"\r\n`)
        currentY += lineHeight // Move down for next line
      }
    })
  }

  // Border line (FOURTH - horizontal line below product/desc/contact)
  const borderY = currentY + 10 // Add 10 dots (~1.25mm) space before border
  const borderWidth = widthDots - mmToDots(4, dpi) // Leave 2mm padding on each side (consistent with text margins)
  const borderX = mmToDots(2, dpi) // Start from 2mm from left (consistent with leftX)
  // BAR x, y, width, height
  commands.push(`BAR ${borderX},${borderY},${borderWidth},${mmToDots(0.5, dpi)}\r\n`)

  // Timestamp (FIFTH - below border, LEFT aligned)
  // Calculate max chars for timestamp with larger right margin
  const timestampAvailableWidthDots = widthDots - leftX - timestampRightMargin
  const timestampMaxChars = Math.floor(timestampAvailableWidthDots / 9.5) // Use same conservative calculation

  const ts = labelData.timestampText || dayjs().format('DD MMM YYYY HH:mm')
  const timestampLines = wrapWords(ts, timestampMaxChars)

  // Print each line of timestamp
  let timestampY = borderY + 15 // 15 dots (~1.9mm) below border
  timestampLines.forEach((line) => {
    if (line && line.trim()) {
      const escapedTs = escapeTSPL(line.trim())
      commands.push(`TEXT ${leftX},${timestampY},"${timestampFont}",0,1,1,"${escapedTs}"\r\n`)
      timestampY += lineHeight // Move down for next line if wrapped
    }
  })

  commands.push(`PRINT 1,1\r\n`) // Print 1 copy, 1 set

  return commands
}

/**
 * Build labeling commands for TSPL format
 * This function handles multiple labels (one per item qty) and returns QZ Tray compatible format
 * @param {Object} receiptData - Receipt data with items
 * @param {Object} options - Print options { paperWidth, dpi, direction }
 * @returns {Array} Array of TSPL command strings for QZ Tray
 */
export const buildLabelingCommands = (receiptData, options = {}) => {
  const { paperWidth, dpi = 203, direction = 0, outletOptions = {} } = options

  const items = receiptData?.items || []
  const tsplCommands = []

  // Bawaan aktif: hanya '0' yang menyembunyikan deskripsi item
  const showItemDescription =
    outletOptions?.print_style_show_product_description?.toString() !== '0'

  // Process each item and create TSPL commands for each sequence
  items.forEach((item) => {
    const qty = Math.abs(item.qty || 1)
    const sequences = Array.from({ length: qty }, (_, idx) => idx + 1)

    sequences.forEach((sequence) => {
      const labelData = {
        qty,
        productName: item.custom_product_name || item.product?.name || '-',
        sequence,
        desc: showItemDescription ? (item.desc ?? item.description) : undefined,
        contactName: isOptionEnabled(outletOptions?.print_style_show_customer)
          ? receiptData.contact?.name
          : undefined,
        tableLabel:
          outletOptions?.print_style_show_order_type === '1'
            ? getTableReceiptLabel(receiptData)
            : undefined,
        timestampText: dayjs().format('DD MMM YYYY HH:mm'),
      }

      // buildTSPLLabel now returns array directly (like buildReceiptCommands)
      const tsplLabelCommands = buildTSPLLabel(labelData, {
        paperWidth,
        dpi,
        direction,
      })

      // Push array of strings for this label
      tsplCommands.push(tsplLabelCommands)
    })
  })

  return tsplCommands
}

export default {
  buildTSPLLabel,
  buildLabelingCommands,
  getLabelDimensions,
  LABEL_PRESETS,
}
